import { DropdownOption } from '@tcl-models/models';
import { ALL_SITES } from '@tempus/trials-types';
import sortBy from 'lodash/sortBy';
import startCase from 'lodash/startCase';
import React, { useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';

import SiteSelectionHeader from '~/components/_common/SiteSelectionHeader/SiteSelectionHeader';
import { allSitesOption } from '~/constants/boron-components';
import { useDispatch, useSelector } from '~/store';
import { creators as siteCreators } from '~/store/site';
import { SiteWithId } from '~/store/site/types';

const dropdownOptionForSite = (site: SiteWithId | null, showAllSites: boolean): DropdownOption | null => {
  if (site && site.name !== ALL_SITES) {
    return { value: site.id, label: site.name };
  }
  if (showAllSites) {
    return allSitesOption;
  }
  return null;
};

const SiteSelectionHeaderContainer: React.FC = () => {
  const dispatch = useDispatch();
  const location = useLocation();

  const canImpersonate = useSelector(({ user }) => user.canImpersonate);
  const selectedUserSite = useSelector(({ site }) => site.selectedUserSite);
  const userSites = useSelector(({ site }) => site.userSites);

  const path = location.pathname;
  const showAllSites =
    canImpersonate &&
    (path.includes('trials') ||
      path.includes('patient-tracker') ||
      path.includes('match-review') ||
      path.includes('all-patients') ||
      path.includes('tempus-nurse') ||
      path.includes('care-pathways'));

  const selectedSite = useMemo(
    () => dropdownOptionForSite(selectedUserSite, showAllSites),
    [selectedUserSite, showAllSites],
  );

  const sortedUserSites = sortBy(Object.entries(userSites || {}), ([, site]) => startCase(site.shortName || site.name));
  const allDropdownOptions: DropdownOption[] = sortedUserSites.map(([id, site]) => ({
    value: id,
    label: site.shortName || site.name,
  }));
  if (showAllSites) {
    allDropdownOptions.unshift(allSitesOption);
  }

  const setSelectedUserSite = (selectedItem: DropdownOption | null) => {
    if (!selectedItem || !userSites) {
      return;
    }
    dispatch(
      siteCreators.setUserSites(userSites, {
        id: selectedItem.value,
        name: selectedItem.label,
      }),
    );
  };

  useEffect(() => {
    if (!showAllSites || selectedUserSite != null || !userSites) {
      return;
    }
    dispatch(
      siteCreators.setUserSites(userSites, {
        id: allSitesOption.value,
        name: allSitesOption.label,
      }),
    );
  }, [dispatch, selectedUserSite, showAllSites, userSites]);

  return <SiteSelectionHeader options={allDropdownOptions} value={selectedSite} onChange={setSelectedUserSite} />;
};

export default SiteSelectionHeaderContainer;
